import classNames from "classnames";

import type { ChoiceOption, OptionId, RichContent } from "../../blockTypes";
import { BLOCK_MESSAGES } from "../../messages";
import PromptBlocks from "./PromptBlocks";

interface ChoiceQuestionProps {
    prompt?: RichContent;
    explanation?: RichContent;
    options: ChoiceOption[];
    selectedIds: OptionId[];
    correctIds: OptionId[];
    isChecked: boolean;
    isCorrect: boolean;
    onSelect: (id: OptionId) => void;
    onCheck: () => void;
}

// How each option looks in each state
const OPTION_STATE_CLASSES = {
    correct: "border-green-500 bg-green-50",
    incorrect: "border-red-500 bg-red-50",
    selected: "border-blue-500 bg-blue-50",
    idle: "border-gray-200 bg-white hover:border-gray-400"
};

// Shared layout of single and multiple choice questions: the blocks only differ in how they select and grade
const ChoiceQuestion = ({
                            prompt,
                            explanation,
                            options,
                            selectedIds,
                            correctIds,
                            isChecked,
                            isCorrect,
                            onSelect,
                            onCheck
                        }: ChoiceQuestionProps) => {
    return (
        <div className="w-full flex flex-col gap-5">
            <div className="text-lg font-semibold leading-8">
                <PromptBlocks blocks={prompt?.blocks} />
            </div>

            <div className="flex flex-col gap-3">
                {options.map((option) => {
                    const selected = selectedIds.includes(option.id);
                    const correct = isChecked && correctIds.includes(option.id);
                    const incorrect = isChecked && selected && !correct;

                    const state = correct
                        ? "correct"
                        : incorrect
                            ? "incorrect"
                            : selected
                                ? "selected"
                                : "idle";

                    return (
                        <button
                            key={option.id}
                            type="button"
                            disabled={isChecked}
                            aria-pressed={selected}
                            onClick={() => onSelect(option.id)}
                            className={classNames(
                                "w-full rounded-xl border p-4 text-start transition",
                                OPTION_STATE_CLASSES[state]
                            )}
                        >
                            {option.text}
                        </button>
                    );
                })}
            </div>

            {!isChecked && (
                <button
                    type="button"
                    disabled={selectedIds.length === 0}
                    onClick={onCheck}
                    className="w-full rounded-xl bg-black px-5 py-3 text-white disabled:opacity-40"
                >
                    {BLOCK_MESSAGES.check}
                </button>
            )}

            {isChecked && (
                <div
                    role="status"
                    className={classNames(
                        "rounded-xl p-4 leading-7",
                        isCorrect ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
                    )}
                >
                    {isCorrect ? BLOCK_MESSAGES.correct : BLOCK_MESSAGES.wrong}
                </div>
            )}

            {isChecked && (explanation?.blocks?.length ?? 0) > 0 && (
                <div className="rounded-xl bg-gray-50 p-4">
                    <PromptBlocks blocks={explanation?.blocks} />
                </div>
            )}
        </div>
    );
};

export default ChoiceQuestion;